import {Decimal} from 'decimal.js';
import {Notification} from 'element-ui';

// Globally managed notifications array.
// There's no better way currently to
// handle this through element ui directly.
// We should probably consider moving
// this to Vuex state.
const notifications = [];

/**
 * Notify pop up wrapper
 *
 * @param {String} message
 * @param {String} type
 * @param {Object} options
 */
export const notify = (message, type, options = {}) => {
  type = type || 'info';
  if (type === 'danger') {
    type = 'error';
  }

  let duration = options.duration || 4500;
  if (type === 'error') {
    duration = 0; // only allow dismissal
  }

  const notification = Notification({
    title: options.title || type,
    message: message,
    type: type,
    duration: duration,
  });

  notifications.push(notification);
};

/**
 * notifyClose closes all notify popups.
 */
export const notifyClose = () => {
  notifications.forEach(n => n.close());

  notifications.splice(0, notifications.length);
};

/**
 * Used for calculating line item discount.
 *
 * @param product
 * @returns {null|{value_type: string, amount: string, description: string, title: string, value: string}}
 */
export const appliedDiscountFactory = (product) => {
  const quantity = Number(product.quantity);
  const variantPrice = Number(product.variant.price);
  const variantNewPrice = Number(product.variant.new_price);

  if (variantPrice === variantNewPrice) {
    return null;
  }

  //Rounding issue somewhere caused this value to be higher than variant price which should be impossible, its maximum value should be the variant price.
  const value = Math.min(variantPrice - variantNewPrice, product.variant.price);
  const amount = quantity * value;

  return {
    'description': 'Custom discount',
    'value_type': 'fixed_amount',
    'value': (new Decimal(value)).toDecimalPlaces(2).toNumber(),
    'amount': (new Decimal(amount)).toDecimalPlaces(2).toNumber(),
    'title': 'Custom',
  };
};

/**
 *
 * @param taxLines
 * @returns {[]}
 */
export const rollUpTaxLines = (taxLines) => {
  let taxLinesRolledUp = [];

  taxLines.forEach(function(line) {
    let found = false;

    taxLinesRolledUp.forEach(function(taxLine, key) {
      if (taxLine.title === line.title) {
        taxLinesRolledUp[key].price = Number(taxLinesRolledUp[key].price) +
            Number(line.price);
        found = true;
      }
    });

    if (!found && line.rate !== 0) {
      taxLinesRolledUp.push({...line});
    }
  });

  return taxLinesRolledUp;
};

/**
 *
 * @param amount
 * @param percent
 * @returns {number}
 */
export const percentOf = (amount, percent) => {
  return new Decimal(amount).mul(percent).toNearest(0.01).toNumber();
};

/**
 *
 * @param lineItem
 * @param logOnOrderItems
 * @param product
 * @returns {*}
 */
export const extractLogOnItem = (lineItem, logOnOrderItems, product) => {
  const productDetails = extractProductDetails(lineItem);
  let logOnOrderItem = logOnOrderItems.find(
      item => (item.style.toUpperCase() === productDetails.style &&
          item.col.toUpperCase() ===
          productDetails.color.replace('FNP', 'F&B') &&
          item.size.toUpperCase() === productDetails.size));

  if (!logOnOrderItem) {
    let shopifyMatchedVariant = product.variants.find(
        variant => (lineItem.variant_id === variant.id));
    const productDetails = extractProductDetails(shopifyMatchedVariant);
    logOnOrderItem = logOnOrderItems.find(
        item => (item.style.toUpperCase() === productDetails.style &&
            item.col.toUpperCase() === productDetails.color &&
            item.size.toUpperCase() === productDetails.size));
  }
  return logOnOrderItem;
};

/**
 * Extracts the D365 item from the sales order lines based on the line item and product details.
 *
 * @param {Object} lineItem - The line item from the order.
 * @param {Array} salesOrderLines - The array of sales order lines from D365.
 * @param {Object} product - The shopify product details.
 * @param index
 * @returns {Object|undefined} The matching sales order line from D365, or undefined if not found.
 */
export const extractD365Item = (lineItem, salesOrderLines, product) => {
  const orderProductDetails = extractProductDetails(lineItem);
  const orderStyleColor = `${orderProductDetails.style}-${orderProductDetails.color}`;
  const matchingLine = salesOrderLines.find(
      line => (line.LineNumber === lineItem.line_number));
  if (matchingLine) {
    return matchingLine;
  }
  return salesOrderLines.find(
      line => (line.ItemNumber.toUpperCase() === orderStyleColor &&
          line.ProductSizeId.toUpperCase() === orderProductDetails.size));
};

/**
 *
 * @param lineItem
 * @returns {{color: *, size: *, style: *}}
 */
export const extractProductDetails = (lineItem) => {
  const skuSplit = lineItem.sku.split('_');

  let productDetails = {
    style: skuSplit[0].toUpperCase(),
    color: skuSplit[1].toUpperCase(),
    size: skuSplit[2].toUpperCase(),
  };
  // For Gift Cards
  if (lineItem.sku.toUpperCase().indexOf('SXGC') !== -1) {
    productDetails = {
      style: lineItem.sku.toUpperCase(),
      color: 'N/A',
      size: 'OS',
    };
  }

  return productDetails;
};

/**
 *
 * @param object
 * @param property
 * @returns {string}
 */
export const extractProperty = (object, property) => {
  return object[property] ? object[property] : '';
};

/**
 *
 * @param taxLines
 * @returns {*}
 */
export const sumTaxLines = (taxLines) => {
  const reducer = (total, taxLine) => (total + Number(taxLine.price));
  return taxLines.reduce(reducer, 0);
};

/**
 * capitalize returns the string with the first character uppercased.
 *
 * @param {String} string
 */
export const capitalize = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Checks and returns whether the given file name has an image extension.
 *
 * @param {String} filename
 * @returns {Boolean}
 */
export const isImage = (filename) => {
  const re = /(?:\.([^.]+))?$/;
  const ext = re.exec(filename);

  return (ext !== undefined && ['jpg', 'jpeg', 'png', 'gif'].includes(ext));
};
